import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Box, styled } from '@mui/material';
import Tab from '@mui/material/Tab';
import {
  CaseNote,
  ConsultTab,
  EconsultActionTab,
  EconsultUserRoles,
  EconsultUtils,
  NoteActionTypes,
  NoteStatusCodes,
  NoteTypes
} from '@oh-vcp/components-common';
import { getCaseActionTitle } from '@oh-vcp/components-common/src/utils/econsultCaseUtils';
import React, { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EconsultCaseComplete from '../econsultCaseComplete/EconsultCaseComplete';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import EconsultCaseProvideConsult, {
  EconsultCaseProvideConsultRef
} from '../econsultCaseProvideConsult/EconsultCaseProvideConsult';
import EconsultCaseRedirect from '../econsultCaseRedirect/EconsultCaseRedirect';
import EconsultCaseReturnConsult from '../econsultCaseReturnConsult/EconsultCaseReturnConsult';
import { useEconsultTabContext } from '../econsultTabContext/EconsultTabContext';
import EconsultDraftWarningDialog from '../econsultDraftWarningDialog/EconsultDraftWarningDialog';
import { useAuth } from '../auth/AuthContext';
import CaseService from '../services/caseService';
import useCaseDetails from '../services/useCaseDetails';
import EconsultSpecialistDraftWarningDialog from '../econsultSpecialistDraftWarningDialog/EconsultSpecialistDraftWarningDialog';

interface EconsultCaseConsultActionDetailsProps {
  options: EconsultActionTab[];
  setPreviousDraftNote: (prevDraftNote: CaseNote | undefined) => void;
  getPreviousDraftNote: () => CaseNote | undefined;
  isDraftChanged: () => boolean | false;
}

const EconsultCaseConsultActionTabPanel = styled(TabPanel)({
  padding: 0,
  paddingTop: 10
});
const EconsultActionTabStyle = styled(Tab)(({ theme }) => ({
  color: theme.palette.primary.main,
  '&.Mui-selected': {
    color: theme.palette.primary.custom3
  },
  '&:hover': {
    borderBottom: '2px solid #77B800'
  }
}));
const EconsultActionTabList = styled(TabList)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.custom3
  }
}));

const EconsultCaseConsultActionDetails: FC<EconsultCaseConsultActionDetailsProps> = ({
  options,
  setPreviousDraftNote,
  getPreviousDraftNote,
  isDraftChanged
}) => {
  const { t } = useTranslation();
  const { caseDetails, userRole } = useEconsultCaseDetailsContext();
  const { user } = useAuth();
  const [reasonForReturning, setReasonForReturning] = useState<string>('');
  const [defaultNote, setDefaultNote] = useState<CaseNote | undefined>(undefined);
  const [callbackTab, setCallbackTab] = useState<ConsultTab | undefined>(undefined);
  const [specialistCallbackTab, setSpecialistCallbackTab] = useState<ConsultTab | undefined>(
    undefined
  );
  const [noteContent, setNoteContent] = useState<string | undefined>(undefined);
  const [deleteAttachmentsCaseId] = useState<number | undefined>(undefined);
  const [isCaseCompleteContentsChanged, setIsCaseCompleteContentsChanged] =
    useState<boolean>(false);
  const { selectedTab, setSelectedTab } = useEconsultTabContext();
  const { refetch: refetchDeleteAttachmentCase } = useCaseDetails(deleteAttachmentsCaseId);
  const provideConsultRef = useRef<EconsultCaseProvideConsultRef>(null);

  useEffect(() => {
    let myNoteType: string;
    if (userRole) {
      myNoteType =
        // eslint-disable-next-line no-nested-ternary
        userRole.indexOf(NoteTypes.referrer) !== -1
          ? NoteTypes.referrer
          : userRole.indexOf(NoteTypes.consultant) !== -1
          ? NoteTypes.consultant
          : NoteTypes.triageAdmin;
    }
    const consultNote = caseDetails?.notes?.find(
      (n) =>
        n.statusCd === NoteStatusCodes.active &&
        (n.actionTypeCd === NoteActionTypes.writeConsult ||
          n.actionTypeCd === NoteActionTypes.askForMoreInfo) &&
        n.noteTypeCd === NoteTypes.consultant
    );
    const draftNote = caseDetails?.notes?.find(
      (n) => n.statusCd === NoteStatusCodes.draft && n.actionTypeCd === NoteActionTypes.addNote
    );
    const draftWriteConsultNote = caseDetails?.notes?.find(
      (n) =>
        n.statusCd === NoteStatusCodes.draft &&
        n.actionTypeCd === NoteActionTypes.writeConsult &&
        n.noteTypeCd === myNoteType
    );
    const askMoreInfoNote = caseDetails?.notes?.find(
      (n) =>
        n.statusCd === NoteStatusCodes.draft &&
        n.actionTypeCd === NoteActionTypes.askForMoreInfo &&
        n.noteTypeCd === myNoteType
    );
    const draftReopenNote = caseDetails?.notes?.find(
      (n) =>
        n.statusCd === NoteStatusCodes.draft &&
        n.actionTypeCd === NoteActionTypes.reopen &&
        n.noteTypeCd === myNoteType
    );
    const draftFinishNote = caseDetails?.notes?.find(
      (n) => n.statusCd === NoteStatusCodes.draft && n.actionTypeCd === NoteActionTypes.finish
    );
    const draftCancelNote = caseDetails?.notes?.find(
      (n) => n.statusCd === NoteStatusCodes.draft && n.actionTypeCd === NoteActionTypes.cancel
    );
    const draftReplyNote = caseDetails?.notes?.find(
      (n) =>
        n.statusCd === NoteStatusCodes.draft &&
        n.actionTypeCd === NoteActionTypes.reply &&
        n.noteTypeCd === myNoteType
    );
    const draftReturnNote = caseDetails?.notes?.find(
      (n) =>
        n.statusCd === NoteStatusCodes.draft &&
        (n.actionTypeCd === NoteActionTypes.unassign ||
          n.actionTypeCd === NoteActionTypes.decline) &&
        n.noteTypeCd === NoteTypes.consultant
    );

    const isCurrentOwner = (note: CaseNote) => {
      return note.ownerId === user?.userid;
    };

    const isCaseSameUserRole = (note: CaseNote) => {
      return (
        ((userRole === EconsultUserRoles.referrerDelegate ||
          userRole === EconsultUserRoles.referrer) &&
          note.noteTypeCd === NoteTypes.referrer) ||
        ((userRole === EconsultUserRoles.consultantDelegate ||
          userRole === EconsultUserRoles.consultant) &&
          note.noteTypeCd === NoteTypes.consultant)
      );
    };

    const addOption = options.find((o) => o.tab === ConsultTab.AddNote);
    const completeOption = options.find((o) => o.tab === ConsultTab.Complete);
    const provideConsultOption = options.find((o) => o.tab === ConsultTab.Provided);
    const askMoreInfoOption = options.find((o) => o.tab === ConsultTab.MoreInfo);
    const reopenOption = options.find((o) => o.tab === ConsultTab.RequestClarification);
    const returnOption = options.find((o) => o.tab === ConsultTab.Returned);
    if (
      draftNote &&
      (isCurrentOwner(draftNote) || isCaseSameUserRole(draftNote)) &&
      addOption &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.AddNote);
      setDefaultNote(draftNote);
    } else if (
      draftReopenNote &&
      (isCurrentOwner(draftReopenNote) || isCaseSameUserRole(draftReopenNote)) &&
      reopenOption &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.RequestClarification);
      setDefaultNote(draftReopenNote);
    } else if (
      draftWriteConsultNote &&
      (isCurrentOwner(draftWriteConsultNote) || isCaseSameUserRole(draftWriteConsultNote)) &&
      provideConsultOption &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.Provided);
      setDefaultNote(draftWriteConsultNote);
    } else if (
      askMoreInfoNote &&
      (isCurrentOwner(askMoreInfoNote) || isCaseSameUserRole(askMoreInfoNote)) &&
      askMoreInfoOption &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.MoreInfo);
      setDefaultNote(askMoreInfoNote);
    } else if (
      draftFinishNote &&
      (isCurrentOwner(draftFinishNote) || isCaseSameUserRole(draftFinishNote)) &&
      completeOption &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.Complete);
      const prevFinishNote = getPreviousDraftNote();
      if (
        prevFinishNote &&
        prevFinishNote.caseId === caseDetails?.caseId &&
        prevFinishNote.actionTypeCd === NoteActionTypes.finish
      ) {
        draftFinishNote.noteContent = prevFinishNote.noteContent;
      }
      setDefaultNote(draftFinishNote);
    } else if (
      draftCancelNote &&
      !consultNote &&
      (isCurrentOwner(draftCancelNote) || isCaseSameUserRole(draftCancelNote)) &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.Cancel);
      setDefaultNote(draftCancelNote);
    } else if (
      draftReplyNote &&
      (isCurrentOwner(draftReplyNote) || isCaseSameUserRole(draftReplyNote)) &&
      !selectedTab &&
      userRole !== NoteTypes.triageAdmin
    ) {
      setSelectedTab(ConsultTab.ProvideMoreInfo);
      setDefaultNote(draftReplyNote);
    } else if (
      draftReturnNote &&
      (isCurrentOwner(draftReturnNote) || isCaseSameUserRole(draftReturnNote)) &&
      returnOption &&
      !selectedTab
    ) {
      setSelectedTab(ConsultTab.Returned);
      setDefaultNote(draftReturnNote);
    } else if (!selectedTab) {
      setDefaultNote(undefined);
    }
  }, [caseDetails?.notes, options]);

  useEffect(() => {
    const deleteDraftNoteAttachments = async () => {
      const deleteAttachmentsCase = await refetchDeleteAttachmentCase();
      const lastDraftNote = deleteAttachmentsCase?.data?.data?.notes?.find(
        (n) => n.statusCd === NoteStatusCodes.draft
      );
      if (!lastDraftNote || !lastDraftNote.attachments || lastDraftNote.attachments.length === 0)
        return;
      lastDraftNote.attachments?.forEach((a) => {
        a.statusCd = NoteStatusCodes.deleted;
      });
      await CaseService.upsertCaseNote(lastDraftNote);
    };

    if (deleteAttachmentsCaseId) {
      deleteDraftNoteAttachments();
    }
  }, [deleteAttachmentsCaseId]);

  const navigateToTab = async (tab: ConsultTab) => {
    if (provideConsultRef && provideConsultRef.current) {
      provideConsultRef.current.saveDraft();
    }
    setDefaultNote(undefined);
    setNoteContent(undefined);
    // setDeleteAttachmentsCaseId(caseDetails?.caseId);
    setSelectedTab(tab);
  };

  const handleTabSelected = async (event: SyntheticEvent<Element, Event>, tab: ConsultTab) => {
    const draftSpecialistNote = caseDetails?.notes?.find(
      (n) => n.statusCd === NoteStatusCodes.draft && n.noteTypeCd === NoteTypes.consultant
    );

    if (tab === ConsultTab.Cancel && draftSpecialistNote) {
      setSpecialistCallbackTab(tab);
    } else if (tab === ConsultTab.Returned && reasonForReturning) {
      setCallbackTab(tab);
    } else if (defaultNote && defaultNote.noteContent) {
      if (defaultNote.actionTypeCd !== 'REOPEN' && defaultNote.actionTypeCd !== 'FINISH') {
        setCallbackTab(tab);
      } else {
        await navigateToTab(tab);
      }
    } else if (
      (noteContent && selectedTab === ConsultTab.RequestClarification) ||
      (isCaseCompleteContentsChanged && selectedTab === ConsultTab.Complete)
    ) {
      setCallbackTab(tab);
    } else {
      const prevFinishNote = getPreviousDraftNote();
      if (prevFinishNote && selectedTab && noteContent) {
        setCallbackTab(tab);
      } else {
        await navigateToTab(tab);
      }
    }
  };

  const handleWarningConfirmed = async () => {
    if (callbackTab) {
      if (provideConsultRef && provideConsultRef.current) {
        provideConsultRef.current.saveDraft();
      }
      setSelectedTab(callbackTab);
      setCallbackTab(undefined);
      setDefaultNote(undefined);
      setNoteContent(undefined);
    }
  };

  const handleSpecialistWarningConfirmed = async () => {
    if (specialistCallbackTab) {
      if (provideConsultRef && provideConsultRef.current) {
        provideConsultRef.current.saveDraft();
      }
      setSelectedTab(specialistCallbackTab);
      setSpecialistCallbackTab(undefined);
      setDefaultNote(undefined);
      setNoteContent(undefined);
    }
  };

  const handleWarningCancel = () => {
    setCallbackTab(undefined);
  };

  const handleSpecialistWarningCancel = () => {
    setSpecialistCallbackTab(undefined);
  };

  const handleOnReasonForReturningChanged = (reason: string) => {
    setReasonForReturning(reason);
  };

  const handleNoteContentChanged = (content: string) => {
    setNoteContent(content);
  };

  const handleCaseCompleteContentsChanged = (isContentsChanged: boolean) => {
    setIsCaseCompleteContentsChanged(isContentsChanged);
  };

  const getTitle = (option: string) => {
    let tab = option;
    if (option && option === 'Add Note') {
      tab = 'AddNoteRef';
      if (options.length === 1) {
        if (options[0].role && options[0].role === 'consultant') {
          tab = 'AddNoteSpec';
        }
      } else {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < options.length; i++) {
          if (options[i].label.indexOf('Consult') !== -1) {
            tab = 'AddNoteSpec';
            break;
          }
        }
      }
    }
    return getCaseActionTitle(tab);
  };
  const tabWidth = EconsultUtils.isMobileDevice() && options.length > 2 ? '100px' : 'inherit';
  return (
    <Box>
      <TabContext value={selectedTab || ''}>
        <EconsultActionTabList onChange={handleTabSelected}>
          {options.map((o) => (
            <EconsultActionTabStyle
              label={o.label}
              value={o.tab}
              icon={<img src={o.imageSrc} alt={o.imageAltText} />}
              iconPosition={o.iconPosition}
              title={getTitle(o.label)}
              style={{ width: tabWidth }}
            />
          ))}
        </EconsultActionTabList>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.Provided}>
          <EconsultCaseProvideConsult
            ref={provideConsultRef}
            actionType={NoteActionTypes.writeConsult}
            draftNote={defaultNote}
            showRecommendation
            setPreviousDraftNote={setPreviousDraftNote}
            onNoteContentChanged={handleNoteContentChanged}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.MoreInfo}>
          <EconsultCaseProvideConsult
            ref={provideConsultRef}
            actionType={NoteActionTypes.askForMoreInfo}
            draftNote={defaultNote}
            setPreviousDraftNote={setPreviousDraftNote}
            onNoteContentChanged={handleNoteContentChanged}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.Returned}>
          <EconsultCaseReturnConsult
            reasonForReturning={reasonForReturning}
            onReasonChanged={handleOnReasonForReturningChanged}
            draftNote={defaultNote}
            onNoteContentChanged={handleNoteContentChanged}
            isDraftChanged={isDraftChanged}
            setPreviousDraftNote={setPreviousDraftNote}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.AddNote}>
          <EconsultCaseProvideConsult
            ref={provideConsultRef}
            actionType={NoteActionTypes.addNote}
            draftNote={defaultNote}
            setPreviousDraftNote={setPreviousDraftNote}
            onNoteContentChanged={handleNoteContentChanged}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.Complete}>
          <EconsultCaseComplete
            actionType="complete"
            draftNote={defaultNote}
            isDraftChanged={isDraftChanged}
            contentsChanged={handleCaseCompleteContentsChanged}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.RequestClarification}>
          <EconsultCaseProvideConsult
            ref={provideConsultRef}
            actionType={NoteActionTypes.reopen}
            draftNote={defaultNote}
            noteFieldLabel={t('Case.consult.enterNoteForFurtherClarification') as string}
            setPreviousDraftNote={setPreviousDraftNote}
            onNoteContentChanged={handleNoteContentChanged}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.Cancel}>
          <EconsultCaseComplete
            actionType="cancel"
            draftNote={defaultNote}
            isDraftChanged={isDraftChanged}
          />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.Redirect}>
          <EconsultCaseRedirect />
        </EconsultCaseConsultActionTabPanel>
        <EconsultCaseConsultActionTabPanel value={ConsultTab.ProvideMoreInfo}>
          <EconsultCaseProvideConsult
            ref={provideConsultRef}
            actionType={NoteActionTypes.reply}
            draftNote={defaultNote}
            setPreviousDraftNote={setPreviousDraftNote}
            onNoteContentChanged={handleNoteContentChanged}
          />
        </EconsultCaseConsultActionTabPanel>
      </TabContext>
      <EconsultDraftWarningDialog
        isOpen={Boolean(callbackTab)}
        onConfirm={handleWarningConfirmed}
        onCancel={handleWarningCancel}
      />
      <EconsultSpecialistDraftWarningDialog
        isOpen={Boolean(specialistCallbackTab)}
        onConfirm={handleSpecialistWarningConfirmed}
        onCancel={handleSpecialistWarningCancel}
      />
    </Box>
  );
};

export default EconsultCaseConsultActionDetails;
